import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, BarChart3, MapPin, Building2, ArrowRight } from 'lucide-react';
import Seo from '@/components/Seo';

// Indicative price-trend data (₹/sqft) — for showcase purposes only
const CITY_DATA = [
  { city: 'Mumbai', priceSqft: 28500, change: 7.2, hotZones: ['Bandra', 'Worli', 'Andheri West'], avgRent: 75000, growth: [22000, 23500, 25800, 27000, 28500] },
  { city: 'Bangalore', priceSqft: 9800, change: 9.5, hotZones: ['Whitefield', 'HSR Layout', 'Indiranagar'], avgRent: 38000, growth: [7400, 8000, 8600, 9200, 9800] },
  { city: 'Delhi NCR', priceSqft: 12400, change: 5.8, hotZones: ['Dwarka', 'Greater Kailash', 'Saket'], avgRent: 42000, growth: [10500, 11000, 11600, 12000, 12400] },
  { city: 'Pune', priceSqft: 8200, change: 8.1, hotZones: ['Hinjewadi', 'Kharadi', 'Baner'], avgRent: 30000, growth: [6500, 6900, 7400, 7800, 8200] },
  { city: 'Hyderabad', priceSqft: 7100, change: 11.3, hotZones: ['Gachibowli', 'Kondapur', 'HITEC City'], avgRent: 28000, growth: [5200, 5700, 6200, 6600, 7100] },
  { city: 'Chennai', priceSqft: 7600, change: 4.2, hotZones: ['Adyar', 'OMR', 'T Nagar'], avgRent: 26000, growth: [6500, 6800, 7100, 7400, 7600] },
  { city: 'Gurgaon', priceSqft: 11800, change: 9.8, hotZones: ['Golf Course Rd', 'Sohna Rd', 'DLF Phase 5'], avgRent: 45000, growth: [8800, 9500, 10300, 11000, 11800] },
  { city: 'Noida', priceSqft: 8900, change: 6.4, hotZones: ['Sector 150', 'Sector 137', 'Sector 78'], avgRent: 32000, growth: [7100, 7600, 8100, 8500, 8900] },
];

const insights = [
  { title: 'Hyderabad leads metro growth', desc: 'IT corridor expansion drives 11.3% YoY price appreciation — fastest among metros.', tag: 'Hot' },
  { title: 'Mumbai luxury demand stable', desc: 'Sub-₹3 Cr segment sees 7.2% growth as MMR commercial recovery boosts buyer confidence.', tag: 'Stable' },
  { title: 'NCR rental yield improves', desc: 'Gurgaon premium leasing returns climb to 3.5% — among highest in India.', tag: 'Yield' },
  { title: 'Tier-2 cities catching up', desc: 'Indore, Coimbatore, Jaipur showing 8–12% growth as remote work drives demand.', tag: 'Trend' },
];

const MarketTrendsPage = () => {
  const [selected, setSelected] = useState(CITY_DATA[0]);
  const max = Math.max(...selected.growth);

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-16" data-testid="market-trends-page">
      <Seo title="India Real Estate Market Insights" description="Track property price trends across 8 Indian metros. See ₹/sqft, YoY change, hot zones, and rental indicators for Mumbai, Bangalore, Delhi, Pune, Hyderabad, Chennai, Gurgaon, Noida." path="/market-trends" />
      <div className="container-luxury">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-xs font-medium mb-3">
            <BarChart3 className="h-3.5 w-3.5" /> Q1 2026 · 8 cities tracked
          </div>
          <h1 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-3">
            Market Insights
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Real-time pulse of India's residential real estate market — average price per sqft, year-over-year change, hot localities, and rental indicators.
          </p>
        </div>

        {/* City grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {CITY_DATA.map((c) => {
            const isUp = c.change > 0;
            const isSelected = selected.city === c.city;
            return (
              <button
                key={c.city}
                onClick={() => setSelected(c)}
                className={`text-left p-5 rounded-sm border-2 transition-all ${isSelected ? 'border-[#D4AF37] bg-white shadow-md' : 'border-transparent bg-white hover:border-[#D4AF37]/40'}`}
                data-testid={`city-card-${c.city.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="h-4 w-4 text-[#D4AF37]" />
                  <h3 className="font-semibold text-[#0F172A]">{c.city}</h3>
                </div>
                <p className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A]">
                  ₹{c.priceSqft.toLocaleString('en-IN')}
                  <span className="text-sm text-muted-foreground font-normal">/sqft</span>
                </p>
                <p className={`text-sm flex items-center gap-1 mt-1 font-medium ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                  {isUp ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {isUp ? '+' : ''}{c.change}% YoY
                </p>
              </button>
            );
          })}
        </div>

        {/* Detail panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
          <div className="lg:col-span-2 bg-white p-8 rounded-sm shadow-sm border border-border/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] flex items-center gap-2">
                <MapPin className="h-6 w-6 text-[#D4AF37]" /> {selected.city}
              </h2>
              <Link to={`/properties?city=${encodeURIComponent(selected.city.split(' ')[0])}`}>
                <Button variant="outline" className="border-[#0F172A]" size="sm">
                  Browse <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </Link>
            </div>

            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">5-year price trend (₹/sqft)</p>
            <div className="flex items-end gap-3 h-48 mb-2">
              {selected.growth.map((v, i) => {
                const h = (v / max) * 100;
                const isLast = i === selected.growth.length - 1;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div className="text-xs font-semibold text-[#0F172A]">₹{v.toLocaleString('en-IN')}</div>
                    <div className={`w-full rounded-t-sm transition-all ${isLast ? 'bg-[#D4AF37]' : 'bg-[#0F172A]/80'}`} style={{ height: `${h}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-end gap-3 text-xs text-muted-foreground">
              {[2022, 2023, 2024, 2025, 2026].map((y) => (
                <div key={y} className="flex-1 text-center">{y}</div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-[#0F172A] text-white p-6 rounded-sm">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2">Average rent (3BHK)</p>
              <p className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#D4AF37]">
                ₹{selected.avgRent.toLocaleString('en-IN')}
                <span className="text-base text-white/70 font-normal">/mo</span>
              </p>
              <p className="text-white/70 text-xs mt-2">
                Indicative gross yield: <strong className="text-white">{((selected.avgRent * 12 / (selected.priceSqft * 1500)) * 100).toFixed(2)}%</strong>
              </p>
            </div>

            <div className="bg-white p-6 rounded-sm border border-border/50">
              <h3 className="font-semibold text-[#0F172A] mb-3 text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4 text-[#D4AF37]" /> Hot Zones
              </h3>
              <ul className="space-y-2">
                {selected.hotZones.map((z) => (
                  <li key={z} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37]" /> {z}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Insights */}
        <h2 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] mb-6 text-center">Market Pulse</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {insights.map((i) => (
            <div key={i.title} className="bg-white p-6 rounded-sm border border-border/50">
              <span className="text-xs font-bold uppercase tracking-wider text-[#D4AF37]">{i.tag}</span>
              <h3 className="font-semibold text-[#0F172A] mt-2 mb-2">{i.title}</h3>
              <p className="text-sm text-muted-foreground">{i.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 text-xs text-muted-foreground">
          * Prices are indicative averages based on listed properties. For accurate market value, consult a Bharat Estate verified agent.
        </div>
      </div>
    </div>
  );
};

export default MarketTrendsPage;
